/**
 * Subpoena Notification Banner
 *
 * REQ-84: Privilege Shield & Subpoena Defense
 * Phase 3: Legal Process Tracking
 *
 * Displays a prominent banner when a user has active legal process
 * affecting their account (subpoena, court order, etc.)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, differenceInDays, isPast } from 'date-fns';
import { X, AlertTriangle, Scale, FileText, Calendar, ChevronRight, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  useUserLegalProcesses,
  useUnacknowledgedLegalNotifications,
  useAcknowledgeLegalNotification,
  formatProcessType,
  formatProcessStatus,
  getStatusColor,
  type LegalProcess,
} from '@/hooks/useLegalProcess';

interface SubpoenaNotificationBannerProps {
  className?: string;
}

export function SubpoenaNotificationBanner({ className }: SubpoenaNotificationBannerProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string[]>([]);

  const { data: legalProcesses = [], isLoading: loadingProcesses } = useUserLegalProcesses();
  const { data: unacknowledgedNotifications = [] } = useUnacknowledgedLegalNotifications();
  const acknowledgeMutation = useAcknowledgeLegalNotification();

  // Filter out dismissed processes
  const activeProcesses = legalProcesses.filter(
    (p) => !dismissed.includes(p.id)
  );

  // Don't render if no active processes or still loading
  if (loadingProcesses || activeProcesses.length === 0) {
    return null;
  }

  // Get the most urgent process (earliest deadline or most recent)
  const urgentProcess = activeProcesses.reduce((prev, current) => {
    if (!prev) return current;

    // Prioritize by compliance deadline
    if (current.compliance_deadline && prev.compliance_deadline) {
      return new Date(current.compliance_deadline) < new Date(prev.compliance_deadline)
        ? current
        : prev;
    }
    if (current.compliance_deadline) return current;
    if (prev.compliance_deadline) return prev;

    // Then by received date (most recent first)
    return new Date(current.received_date) > new Date(prev.received_date)
      ? current
      : prev;
  }, null as LegalProcess | null);

  if (!urgentProcess) return null;

  const deadlineDays = urgentProcess.compliance_deadline
    ? differenceInDays(new Date(urgentProcess.compliance_deadline), new Date())
    : null;

  const isDeadlineUrgent = deadlineDays !== null && deadlineDays <= 7;
  const isDeadlinePast = urgentProcess.compliance_deadline
    ? isPast(new Date(urgentProcess.compliance_deadline))
    : false;

  const handleDismiss = (id: string) => {
    setDismissed((prev) => [...prev, id]);
  };

  const handleViewDetails = () => {
    navigate('/legal-process');
  };

  const handleAcknowledge = async (notificationId: string) => {
    await acknowledgeMutation.mutateAsync(notificationId);
  };

  // Find unacknowledged notification for this process
  const pendingNotification = unacknowledgedNotifications.find(
    (n: any) => n.legal_process_id === urgentProcess.id
  );

  return (
    <div className={className}>
      <Alert
        variant="destructive"
        className={`
          relative border-2
          ${isDeadlineUrgent || isDeadlinePast
            ? 'bg-red-50 border-red-300'
            : 'bg-amber-50 border-amber-300'}
        `}
      >
        {/* Dismiss button (only if not requiring acknowledgment) */}
        {!pendingNotification && (
          <button
            onClick={() => handleDismiss(urgentProcess.id)}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Icon */}
        <div className="flex items-start gap-4">
          <div
            className={`
              p-2 rounded-lg
              ${isDeadlineUrgent || isDeadlinePast
                ? 'bg-red-100'
                : 'bg-amber-100'}
            `}
          >
            <Scale
              className={`
                h-6 w-6
                ${isDeadlineUrgent || isDeadlinePast
                  ? 'text-red-600'
                  : 'text-amber-600'}
              `}
            />
          </div>

          <div className="flex-1 min-w-0">
            <AlertTitle
              className={`
                text-base font-semibold flex items-center gap-2 flex-wrap
                ${isDeadlineUrgent || isDeadlinePast
                  ? 'text-red-800'
                  : 'text-amber-800'}
              `}
            >
              <span>Legal Process Alert</span>
              <Badge variant="outline" className={getStatusColor(urgentProcess.status)}>
                {formatProcessStatus(urgentProcess.status)}
              </Badge>
              {activeProcesses.length > 1 && (
                <Badge variant="secondary" className="text-xs">
                  +{activeProcesses.length - 1} more
                </Badge>
              )}
            </AlertTitle>

            <AlertDescription className="mt-2 space-y-3">
              {/* Process details */}
              <div
                className={`
                  text-sm
                  ${isDeadlineUrgent || isDeadlinePast
                    ? 'text-red-700'
                    : 'text-amber-700'}
                `}
              >
                <p className="font-medium">
                  {formatProcessType(urgentProcess.process_type)} received from{' '}
                  {urgentProcess.issuing_authority}
                </p>

                {urgentProcess.case_name && (
                  <p className="text-sm opacity-90">
                    Case: {urgentProcess.case_name}
                    {urgentProcess.case_number && ` (${urgentProcess.case_number})`}
                  </p>
                )}
              </div>

              {/* Key information cards */}
              <div className="flex flex-wrap gap-3 mt-2">
                {/* Deadline */}
                {urgentProcess.compliance_deadline && (
                  <div
                    className={`
                      flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
                      ${isDeadlinePast
                        ? 'bg-red-100 text-red-800'
                        : isDeadlineUrgent
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-800'}
                    `}
                  >
                    <Calendar className="h-4 w-4" />
                    <span>
                      Deadline:{' '}
                      {format(new Date(urgentProcess.compliance_deadline), 'MMM d, yyyy')}
                      {isDeadlinePast && ' (PAST DUE)'}
                      {!isDeadlinePast && deadlineDays !== null && deadlineDays <= 14 && (
                        <span className="font-semibold"> ({deadlineDays} days)</span>
                      )}
                    </span>
                  </div>
                )}

                {/* Privilege status */}
                {urgentProcess.privilege_asserted && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-green-100 text-green-800">
                    <Shield className="h-4 w-4" />
                    <span>Privilege Asserted</span>
                  </div>
                )}

                {/* Motion filed */}
                {urgentProcess.motion_to_quash_filed && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-purple-100 text-purple-800">
                    <FileText className="h-4 w-4" />
                    <span>Motion to Quash Filed</span>
                  </div>
                )}

                {/* Hearing scheduled */}
                {urgentProcess.hearing_date && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm bg-blue-100 text-blue-800">
                    <Calendar className="h-4 w-4" />
                    <span>
                      Hearing: {format(new Date(urgentProcess.hearing_date), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
              </div>

              {/* Recommendation */}
              <div
                className={`
                  flex items-start gap-2 text-sm
                  ${isDeadlineUrgent || isDeadlinePast
                    ? 'text-red-700'
                    : 'text-amber-700'}
                `}
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {urgentProcess.privilege_asserted
                    ? 'SettleWise is defending your privacy. Contact your attorney for updates.'
                    : 'Contact your attorney immediately to discuss this legal matter.'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  size="sm"
                  variant={isDeadlineUrgent || isDeadlinePast ? 'destructive' : 'default'}
                  onClick={handleViewDetails}
                  className="gap-1"
                >
                  View Details
                  <ChevronRight className="h-4 w-4" />
                </Button>

                {pendingNotification && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleAcknowledge(pendingNotification.id)}
                    disabled={acknowledgeMutation.isPending}
                  >
                    {acknowledgeMutation.isPending ? 'Acknowledging...' : 'I Understand'}
                  </Button>
                )}
              </div>
            </AlertDescription>
          </div>
        </div>
      </Alert>
    </div>
  );
}

export default SubpoenaNotificationBanner;
